import {
  activateVersion,
  getJobs,
  getMaterialization,
  getPublication,
  getValidation,
  launchProcessing,
  materializeActive,
  publishVersion,
  safeErrorMessage,
  type DatasetLocation,
} from "./api";

export type FullPipelineStep =
  | "validation"
  | "semanticization"
  | "publication"
  | "activation"
  | "materialization";

type TrackableJob = {
  id: string;
  state: string;
  versionId?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
};

export async function runFullDataPipeline(
  location: DatasetLocation,
  onStep: (step: FullPipelineStep) => void,
  wait: () => Promise<void> = waitBeforeNextCheck,
) {
  onStep("validation");
  const validationLaunch = await launchProcessing(location, "validate");
  await waitForSuccessfulJob(location, validationLaunch.job, wait);
  const validation = await getValidation(location);
  if (!validation?.usable) {
    throw new FullPipelineError("La validation n’a pas produit de résultat exploitable.");
  }

  onStep("semanticization");
  const semanticizationLaunch = await launchProcessing(location, "semanticize");
  const semanticizationJob = await waitForSuccessfulJob(location, semanticizationLaunch.job, wait);
  const versionId = semanticizationLaunch.version?.id ?? semanticizationJob.versionId;
  if (!versionId) {
    throw new FullPipelineError("La sémantisation n’a pas produit de version candidate.");
  }

  onStep("publication");
  const publicationLaunch = await publishVersion(location, versionId);
  if (publicationLaunch.publication.job) {
    await waitForSuccessfulJob(location, publicationLaunch.publication.job, wait);
  }
  const publication = await getPublication(location, versionId);
  if (publication.status !== "published" || !publication.verified) {
    throw new FullPipelineError("La publication n’a pas été vérifiée.");
  }

  onStep("activation");
  const activation = await activateVersion(location, versionId);
  if (activation.activation.activeVersion?.id !== versionId) {
    throw new FullPipelineError("La version publiée n’a pas été activée.");
  }

  onStep("materialization");
  const materializationLaunch = await materializeActive(location);
  await waitForSuccessfulJob(location, materializationLaunch.job, wait);
  const materialization = await getMaterialization(location);
  if (!materialization.aligned || materialization.materializedVersion?.id !== versionId) {
    throw new FullPipelineError("La version active n’a pas été matérialisée.");
  }

  return versionId;
}

export function fullPipelineErrorMessage(error: unknown) {
  if (error instanceof FullPipelineError) return error.message;
  return safeErrorMessage(error);
}

export function fullPipelineStepLabel(step: FullPipelineStep | null, restartFromValidation = false) {
  if (step === "validation") return "Validation en cours…";
  if (step === "semanticization") return "Sémantisation en cours…";
  if (step === "publication") return "Publication en cours…";
  if (step === "activation") return "Activation en cours…";
  if (step === "materialization") return "Matérialisation en cours…";
  if (restartFromValidation) return "Recommencer depuis la validation";
  return "Tout exécuter";
}

async function waitForSuccessfulJob(
  location: DatasetLocation,
  initialJob: TrackableJob,
  wait: () => Promise<void>,
) {
  let job = initialJob;
  while (!isSuccessful(job.state)) {
    if (isFailed(job.state)) throw jobFailure(job);
    await wait();
    const jobs = await getJobs(location);
    const updated = jobs.items.find((item) => item.id === job.id);
    if (updated) job = updated;
  }
  return job;
}

function isSuccessful(state: string) {
  return state === "succeeded" || state === "completed";
}

function isFailed(state: string) {
  return state === "failed" || state === "cancelled" || state === "superseded";
}

function jobFailure(job: TrackableJob) {
  if (job.errorMessage) return new FullPipelineError(job.errorMessage);
  if (job.errorCode) return new FullPipelineError(`Le traitement a échoué : ${job.errorCode}.`);
  if (job.state === "cancelled") return new FullPipelineError("Le traitement a été annulé.");
  if (job.state === "superseded") return new FullPipelineError("Le traitement a été remplacé par une demande plus récente.");
  return new FullPipelineError("Le traitement a échoué.");
}

function waitBeforeNextCheck() {
  return new Promise<void>((resolve) => window.setTimeout(resolve, 2_000));
}

class FullPipelineError extends Error {}
